import { Button, IconButton } from "@mui/material";
import React, { useCallback, useContext } from "react";
import IconComponent from "../../../common/components/IconComponent";
import BundleContext from "../../Contexts/BundleContext";
import useObservedExpression from "../../Hooks/useObservedExpression";
import useObservedText from "../../Hooks/useObservedText";
import { ButtonModel, ButtonVariant } from "../../Models/ButtonModel";
import IComponentProps from "../IComponentProps";
import getComponentLayout from "../Table/getComponentLayout";
import ViewComponent from "../View/ViewComponent";
import getButtonVariant from "./getButtonVariant";

type ButtonComponentProps = IComponentProps<ButtonModel>;

const ButtonComponent: React.FC<ButtonComponentProps> = ({ component, context }) => {
  const bundle = useContext(BundleContext);

  const componentLayoutSx = getComponentLayout(component);

  const [open, setOpen] = React.useState(false);
  const [input, setInput] = React.useState<Record<string, any> | undefined>(undefined);
  const [output, setOutput] = React.useState<Record<string, any> | undefined>(undefined);
  const [isLoading, setIsLoading] = React.useState(false);

  const [disabled] = useObservedExpression<boolean | undefined>(component.Disabled, context, "ButtonComponent");

  const label = useObservedText(component.Label, context, "ButtonComponent");
  const iconName = useObservedText(component.IconName, context, "ButtonComponent");

  const handleClick = async () => {
    setIsLoading(true);
    try {
      if (component.OnSelected) {
        await context.Evaluator.evaluate(component.OnSelected, context);
      }

      if (component.View) {
        let evaluatedInput: Record<string, any> = {};
        if (component.View?.Input) {
          evaluatedInput = await context.Evaluator.evaluate(component.View.Input, context);
          setInput(evaluatedInput);
          console.log("[ButtonComponent] clicked, evaluated input:", evaluatedInput);
        }
        setOpen(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOutput = useCallback(
    async (output: Record<string, any>) => {
      setOutput(output);

      console.log("[ButtonComponent] received output from View:", output);

      if (component.OnViewReturned) {
        setIsLoading(true);
        try {
          const eventContext = context.CreateChild();
          eventContext.VariableScope.set("$output", output);
          await eventContext.Evaluator.evaluate(component.OnViewReturned, eventContext);
        } finally {
          setIsLoading(false);
        }
      }
      setOpen(false);
    },
    [setOutput, context, component.OnViewReturned]
  );

  return (
    <>
      {component.Variant === ButtonVariant.IconButton ? (
        <IconButton onClick={handleClick} disabled={isLoading || disabled === true} loading={isLoading} title={label} sx={componentLayoutSx}>
          <IconComponent iconName={iconName} size="small" />
        </IconButton>
      ) : (
        <Button
          sx={componentLayoutSx}
          startIcon={<IconComponent iconName={iconName} />}
          onClick={handleClick}
          disabled={isLoading || disabled === true}
          variant={getButtonVariant(component.Variant)}
          loading={isLoading}
        >
          {label}
        </Button>
      )}

      {component.View?.Id && open && <ViewComponent component={bundle.ViewById[component.View.Id]} open={open} input={input} setOutput={handleOutput} />}
    </>
  );
};

export default ButtonComponent;
